import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { InventoryFilter } from '../../../types/inventory.types';
import type { ListPageActiveFilterChip } from '../../common/list-page';

export interface UseInventoryFiltersProps {
  filters: InventoryFilter;
  setFilters: React.Dispatch<React.SetStateAction<InventoryFilter>>;
  appliedFilters: InventoryFilter;
  onClearAppliedField: (field: keyof InventoryFilter) => void;
  onSearch: (nextFilters?: InventoryFilter) => void;
  onReset: () => void;
  suppliers?: Array<{ id: number; name: string }>;
}

const RECORD_STATUS_LABELS: Record<string, string> = {
  completed: 'Completado',
  pending: 'Pendiente',
  cancelled: 'Cancelado',
};

export const useInventoryFilters = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  onSearch,
  onReset,
  suppliers = [],
}: UseInventoryFiltersProps) => {
  // Estado local para filtros expandibles
  const [expandedFilters, setExpandedFilters] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const quickSearchRef = useRef(quickSearch);

  // Mantener ref actualizado
  useEffect(() => {
    quickSearchRef.current = quickSearch;
  }, [quickSearch]);

  // Sincronizar quickSearch con productName cuando cambia desde fuera (incl. clear chip)
  useEffect(() => {
    const next = filters.productName || '';
    if (next !== quickSearchRef.current) {
      setQuickSearch(next);
    }
  }, [filters.productName]);

  // Manejar cambios en inputs de texto
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFilters((prev: InventoryFilter) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  // Manejar cambios en selects
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const name = e.target.name as string;
      const value = e.target.value as string;
      setFilters((prev: InventoryFilter) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  // Manejar cambios en fechas
  const handleDateChange = useCallback(
    (name: string, value: string) => {
      setFilters((prev: InventoryFilter) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  // Toggle de filtros expandibles
  const toggleFilters = useCallback(() => {
    setExpandedFilters((prev) => !prev);
  }, []);

  // Manejar búsqueda rápida
  const handleQuickSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuickSearch(event.target.value);
  }, []);

  // Ejecutar búsqueda rápida — pass next filters so parent can apply without draft race
  const handleQuickSearch = useCallback(() => {
    const next: InventoryFilter = {
      ...filters,
      productName: quickSearch.trim(),
    };
    setFilters(next);
    onSearch(next);
  }, [quickSearch, filters, setFilters, onSearch]);

  // Manejar tecla Enter en búsqueda rápida
  const handleQuickSearchKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleQuickSearch();
      }
    },
    [handleQuickSearch],
  );

  // Limpiar búsqueda
  const handleClearSearch = useCallback(() => {
    setQuickSearch('');
    onReset();
  }, [onReset]);

  // Verificar si hay filtros activos (aplicados)
  const hasActiveFilters = useCallback(() => {
    return Object.values(appliedFilters).some(
      (value) => value !== '' && value !== null && value !== undefined,
    );
  }, [appliedFilters]);

  // Obtener contador de filtros activos
  const getActiveFiltersCount = useCallback(() => {
    return Object.values(appliedFilters).filter(
      (value) => value !== '' && value !== null && value !== undefined,
    ).length;
  }, [appliedFilters]);

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.productName) {
      chips.push({
        key: 'productName',
        label: `Búsqueda: ${applied.productName}`,
        onRemove: () => onClearAppliedField('productName'),
      });
    }
    if (applied.startDate) {
      chips.push({
        key: 'startDate',
        label: `Desde: ${applied.startDate}`,
        onRemove: () => onClearAppliedField('startDate'),
      });
    }
    if (applied.endDate) {
      chips.push({
        key: 'endDate',
        label: `Hasta: ${applied.endDate}`,
        onRemove: () => onClearAppliedField('endDate'),
      });
    }
    if (applied.supplierId) {
      const supplierName =
        suppliers.find((s) => s.id.toString() === applied.supplierId)?.name || applied.supplierId;
      chips.push({
        key: 'supplierId',
        label: `Proveedor: ${supplierName}`,
        onRemove: () => onClearAppliedField('supplierId'),
      });
    }
    if (applied.recordStatus) {
      const statusLabel = RECORD_STATUS_LABELS[applied.recordStatus] || applied.recordStatus;
      chips.push({
        key: 'recordStatus',
        label: `Estado: ${statusLabel}`,
        onRemove: () => onClearAppliedField('recordStatus'),
      });
    }

    return chips;
  }, [appliedFilters, suppliers, onClearAppliedField]);

  return {
    // Estado
    expandedFilters,
    quickSearch,
    setQuickSearch,

    // Handlers
    handleChange,
    handleSelectChange,
    handleDateChange,
    toggleFilters,
    handleQuickSearchChange,
    handleQuickSearch,
    handleQuickSearchKeyPress,
    handleClearSearch,

    // Utilidades
    hasActiveFilters: hasActiveFilters(),
    activeFiltersCount: getActiveFiltersCount(),
    activeFilterChips,
  };
};
